/**
 * AgentSimulator Component
 * Interactive chat simulator for testing agents
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  IconX,
  IconSend,
  IconRobot,
  IconTrash,
  IconPhoto,
  IconMicrophone,
  IconPlayerStop,
  IconShare,
  IconFileTypePdf,
  IconDownload,
  IconBug,
  IconSparkles,
  IconVariable,
  IconHistory,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';

import { useAgentsFlow } from '../contexts/AgentsFlowContext';
import { useAgentsFlowApi } from '../hooks/useAgentsFlowApi';
import PublicLinkModal from '../../../jsx/components/PublicLinkModal';
import PlaygroundSessionsModal from './PlaygroundSessionsModal';
import DebugTracePanel from './DebugTracePanel';
import CopilotDrawer from './CopilotDrawer';
import VariablesPanel from '../../../jsx/components/Playground/components/VariablesPanel';
import useCollectedVariables from '../../../jsx/components/Playground/hooks/useCollectedVariables';

// ===============================================
// TYPING INDICATOR
// ===============================================

const TypingIndicator = () => (
  <div className="simulator-typing">
    <span className="simulator-typing-dot" />
    <span className="simulator-typing-dot" />
    <span className="simulator-typing-dot" />
  </div>
);

// ===============================================
// MESSAGE COMPONENT
// ===============================================

const tryParseMedia = (content) => {
  if (!content || typeof content !== 'string') return null;
  const trimmed = content.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && parsed.type === 'media') return parsed;
  } catch {
    // não é JSON
  }
  return null;
};

const MediaContent = ({ media }) => {
  const isImage = media.media_type === 'image';
  const isAudio = media.media_type === 'audio';

  return (
    <div className="simulator-media">
      {isAudio ? (
        <div className="simulator-media__audio">
          <audio controls preload="metadata" src={media.url} />
          {media.filename && (
            <span className="simulator-media__audio-name">{media.filename}</span>
          )}
        </div>
      ) : isImage ? (
        <a href={media.url} target="_blank" rel="noopener noreferrer">
          <img
            src={media.url}
            alt={media.filename || 'Imagem'}
            className="simulator-media__image"
          />
        </a>
      ) : (
        <a
          href={media.url}
          target="_blank"
          rel="noopener noreferrer"
          className="simulator-media__document"
        >
          <IconFileTypePdf size={28} />
          <span className="simulator-media__filename">
            {media.filename || 'Documento'}
          </span>
          <IconDownload size={16} className="simulator-media__download" />
        </a>
      )}
      {media.caption && (
        <div className="simulator-media__caption">{media.caption}</div>
      )}
    </div>
  );
};

const SimulatorMessage = ({ message, showMeta }) => {
  const { type, content, metadata, timestamp } = message;

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const mediaData = tryParseMedia(content);

  return (
    <div className={`simulator-message ${type}`}>
      {mediaData ? (
        <MediaContent media={mediaData} />
      ) : (
        <div className="simulator-message-content">{content}</div>
      )}
      <div className="simulator-message-time">{formatTime(timestamp)}</div>
    </div>
  );
};

// ===============================================
// MAIN SIMULATOR COMPONENT
// ===============================================

const AgentSimulator = ({ agent, onClose, embedded = true }) => {
  const { state, clearSimulator, setSimulatorOptions } = useAgentsFlow();
  const {
    sendTestMessage,
    sendTestImage,
    sendTestAudio,
    resetMemory,
    archivePlaygroundSession,
    getPlaygroundSession,
  } = useAgentsFlowApi();
  const { simulator } = state;

  const [inputValue, setInputValue] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotFocusedMessage, setCopilotFocusedMessage] = useState(null);
  const [copilotClearKey, setCopilotClearKey] = useState(0);
  const [variablesPanelOpen, setVariablesPanelOpen] = useState(false);
  const [sessionsModalOpen, setSessionsModalOpen] = useState(false);
  const [isReplaying, setIsReplaying] = useState(false);

  // Painel de variáveis — drawer overlay acionado pelo botão no header.
  // Alimentado pelo response do test_message a cada turno.
  const {
    variables: collectedVariables,
    collectedData,
    apiResponses,
    declaredVariables,
    recentlyChangedKeys,
    updateFromResponse: updateCollectedVariables,
    reset: resetCollectedVariables,
  } = useCollectedVariables(agent?.id);

  // Contagem mostrada no badge do botão toggle — conta apenas variáveis
  // declaradas que JÁ foram preenchidas (ignora "aguardando" e runtime).
  const variablesPopulatedCount = useMemo(() => {
    return declaredVariables.filter((d) => {
      const valueSource = (d.source === 'extract' || d.source === 'set_variable')
        ? collectedVariables
        : collectedData;
      return d.name in valueSource;
    }).length;
  }, [declaredVariables, collectedVariables, collectedData]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [simulator.messages]);

  // Focus input on mount and after API response
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!simulator.isTyping) {
      inputRef.current?.focus();
    }
  }, [simulator.isTyping]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      if (recordedAudio) URL.revokeObjectURL(recordedAudio);
    };
  }, []);

  // -----------------------------------------------
  // Text
  // -----------------------------------------------

  const handleSend = useCallback(async () => {
    const message = inputValue.trim();
    if (!message || simulator.isTyping) return;

    setInputValue('');

    try {
      const result = await sendTestMessage(agent.id, message, simulator.debugMode);
      // Alimenta o painel lateral. result já vem do endpoint test_message com
      // variables, collected_data, api_responses e declared_variables (debug=true).
      if (result) {
        updateCollectedVariables(result);
      }
    } catch (error) {
      console.error('Error sending test message:', error);
    }
  }, [inputValue, agent, simulator.isTyping, simulator.debugMode, sendTestMessage, updateCollectedVariables]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // -----------------------------------------------
  // Image
  // -----------------------------------------------

  const handleImageSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    // Reset file input so same file can be re-selected
    e.target.value = '';
  }, []);

  const clearSelectedImage = useCallback(() => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
  }, [imagePreview]);

  const handleSendImage = useCallback(async () => {
    if (!imageFile || simulator.isTyping) return;
    const file = imageFile;
    clearSelectedImage();
    try {
      const result = await sendTestImage(agent.id, file);
      // Alimenta o painel lateral também em envios de mídia.
      // Sem isso, variáveis extraídas pelo media_analysis (ex:
      // COMPROVANTE_VALIDO) não atualizam no painel até a próxima
      // mensagem de texto.
      if (result) {
        updateCollectedVariables(result);
      }
    } catch (error) {
      console.error('Error sending image:', error);
    }
  }, [imageFile, agent, simulator.isTyping, sendTestImage, clearSelectedImage, updateCollectedVariables]);

  // -----------------------------------------------
  // Audio
  // -----------------------------------------------

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setRecordedAudio(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      Swal.fire({
        title: 'Erro',
        text: 'Não foi possível acessar o microfone. Verifique as permissões do navegador.',
        icon: 'error',
      });
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const clearRecordedAudio = useCallback(() => {
    if (recordedAudio) URL.revokeObjectURL(recordedAudio);
    setRecordedAudio(null);
    setAudioBlob(null);
  }, [recordedAudio]);

  const handleSendAudio = useCallback(async () => {
    if (!audioBlob || simulator.isTyping) return;
    const blob = audioBlob;
    clearRecordedAudio();
    try {
      const result = await sendTestAudio(agent.id, blob);
      if (result) {
        updateCollectedVariables(result);
      }
    } catch (error) {
      console.error('Error sending audio:', error);
    }
  }, [audioBlob, agent, simulator.isTyping, sendTestAudio, clearRecordedAudio, updateCollectedVariables]);

  // -----------------------------------------------
  // Memory reset
  // -----------------------------------------------

  const handleResetMemory = useCallback(async () => {
    const result = await Swal.fire({
      title: 'Limpar memória do agente?',
      text: 'A conversa atual será arquivada (até 10 sessões salvas) para você poder reexecutar depois. Depois, o histórico do agente será limpo.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sim, arquivar e limpar',
      cancelButtonText: 'Cancelar',
    });

    if (!result.isConfirmed) return;

    // Arquivar sessão atual ANTES de limpar — só se houver pelo menos
    // uma mensagem do usuário (sessão "vazia" do welcome não conta).
    const userMessages = (simulator.messages || []).filter(
      (m) => m && m.type === 'user' && (m.content || '').toString().trim().length > 0,
    );

    if (userMessages.length > 0) {
      try {
        await archivePlaygroundSession(agent.id, {
          messages: simulator.messages,
        });
      } catch (error) {
        const isLimit = error?.response?.status === 409;
        if (isLimit) {
          await Swal.fire({
            title: 'Limite de sessões atingido',
            text: 'Você já tem 10 sessões salvas. Abra "Sessões salvas" e apague alguma antes de limpar a memória.',
            icon: 'warning',
            confirmButtonText: 'Abrir sessões salvas',
          });
          setSessionsModalOpen(true);
          return; // NÃO limpa memória — usuário precisa liberar espaço primeiro
        }
        await Swal.fire({
          title: 'Erro ao arquivar',
          text: 'Não foi possível salvar a sessão atual. A memória NÃO foi limpa.',
          icon: 'error',
        });
        return;
      }
    }

    try {
      await resetMemory(agent.id);
      clearSimulator();
      setCopilotClearKey((k) => k + 1);
      setCopilotFocusedMessage(null);
      Swal.fire({
        title: userMessages.length > 0 ? 'Sessão salva e memória limpa!' : 'Memória limpa!',
        text:
          userMessages.length > 0
            ? 'A conversa foi arquivada. Use "Sessões salvas" para reexecutar.'
            : 'O histórico do agente foi apagado.',
        icon: 'success',
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: 'Erro',
        text: 'Não foi possível limpar a memória do agente.',
        icon: 'error',
      });
    }
  }, [agent, resetMemory, clearSimulator, simulator.messages, archivePlaygroundSession]);

  // Replay sequencial — para cada user message da sessão salva, dispara
  // sendTestMessage e aguarda. Reusa o pipeline normal do simulator
  // (typing indicator, variáveis coletadas, etc.) sem precisar de nada
  // novo no backend.
  const handleReplaySession = useCallback(async (sessionId) => {
    if (!agent?.id || !sessionId) return;
    setIsReplaying(true);
    try {
      const session = await getPlaygroundSession(agent.id, sessionId);
      const userMessages = (session.messages || []).filter(
        (m) => m && m.type === 'user' && (m.content || '').toString().trim().length > 0,
      );
      // Limpar conversa atual antes do replay para não misturar com sessão antiga.
      try {
        await resetMemory(agent.id);
      } catch (e) {
        // Se não conseguir resetar, ainda assim tenta replay (a memória pode estar OK)
        console.warn('Reset antes do replay falhou:', e);
      }
      clearSimulator();
      for (const msg of userMessages) {
        const content = (msg.content || '').toString();
        // sendTestMessage adiciona a user message ao simulator e aguarda
        // a resposta da LLM (handleAgentResponse). Sequencial: await aqui
        // já cumpre a regra "cada mensagem aguarda a resposta da LLM".
        await sendTestMessage(agent.id, content, simulator.debugMode);
      }
      Swal.fire({
        icon: 'success',
        title: 'Replay concluído',
        text: `${userMessages.length} mensagem(ns) reexecutada(s).`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro no replay',
        text: 'Não foi possível executar a sessão completa.',
      });
    } finally {
      setIsReplaying(false);
    }
  }, [agent, getPlaygroundSession, resetMemory, clearSimulator, sendTestMessage, simulator.debugMode]);

  // Determine if there's media ready to send
  const hasMedia = imageFile || audioBlob;

  return (
    <div className={`simulator-copilot-wrapper ${copilotOpen ? 'simulator-copilot-wrapper--open' : ''}`}>
      <div className={`simulator-container ${embedded ? 'simulator-container--embedded' : ''}`}>
        {/* Header */}
        <div className="simulator-header">
          <div className="simulator-header-info">
            <div className="simulator-avatar">
              <IconRobot size={20} />
            </div>
            <div>
              <h4 className="simulator-title">
                {agent?.identity_config?.agent_name || agent?.name || 'Agente'}
              </h4>
              <p className="simulator-subtitle">Modo de teste</p>
            </div>
          </div>
          <div className="simulator-header__toolbar">
            <button
              className={`simulator-header__btn simulator-header__btn--debug ${simulator.debugMode ? 'active' : ''}`}
              onClick={() => setSimulatorOptions({ debugMode: !simulator.debugMode })}
              title={simulator.debugMode ? 'Desativar modo debug' : 'Ativar modo debug'}
            >
              <IconBug size={17} />
            </button>
              <button
                className={`simulator-header__btn simulator-header__btn--copilot ${copilotOpen ? 'active' : ''}`}
                onClick={() => { setCopilotOpen(!copilotOpen); setCopilotFocusedMessage(null); }}
                title={copilotOpen ? 'Fechar Copilot' : 'Abrir Copilot'}
              >
                <IconSparkles size={17} />
              </button>
              <button
                className={`simulator-header__btn simulator-header__btn--variables ${variablesPanelOpen ? 'active' : ''}`}
                onClick={() => setVariablesPanelOpen((v) => !v)}
                title={variablesPanelOpen ? 'Fechar variáveis' : 'Ver variáveis coletadas'}
              >
                <IconVariable size={17} />
                {variablesPopulatedCount > 0 && (
                  <span className="simulator-header__btn-badge">{variablesPopulatedCount}</span>
                )}
              </button>
            <div className="simulator-header__separator" />
            <button
              className="simulator-header__btn"
              onClick={() => setSessionsModalOpen(true)}
              title="Sessões de teste salvas"
              disabled={isReplaying}
            >
              <IconHistory size={17} />
            </button>
            <button
              className="simulator-header__btn"
              onClick={() => setShowPublicLinkModal(true)}
              title="Compartilhar"
            >
              <IconShare size={17} />
            </button>
            <button
              className="simulator-header__btn"
              onClick={handleResetMemory}
              title="Limpar memória do agente (arquiva a sessão atual)"
              disabled={isReplaying}
            >
              <IconTrash size={17} />
            </button>
            <div className="simulator-header__separator" />
            <button className="simulator-header__btn" onClick={onClose} title="Fechar">
              <IconX size={17} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="simulator-messages">
          {simulator.messages.length === 0 ? (
            <div className="simulator-welcome">
              <div className="simulator-welcome__icon">
                <IconRobot size={28} />
              </div>
              <h4 className="simulator-welcome__name">
                {agent?.identity_config?.agent_name || 'Assistente Virtual'}
              </h4>
              <p className="simulator-welcome__text">
                Como posso ajudar você hoje?
              </p>
            </div>
          ) : (
            simulator.messages.map((msg) => (
              <React.Fragment key={msg.id}>
                <div>
                  <SimulatorMessage
                    message={msg}
                    showMeta={simulator.showCapabilities}
                  />
                  {simulator.debugMode && msg.type === 'agent' && msg.metadata?.debug_trace && (
                    <button
                      className="copilot-msg-btn"
                      onClick={() => {
                        setCopilotFocusedMessage(msg);
                        setCopilotOpen(true);
                      }}
                      title="Analisar com Copilot"
                    >
                      <IconSparkles size={12} /> Analisar
                    </button>
                  )}
                </div>
                {msg.type === 'agent' && msg.metadata?.debug_trace && (
                  <DebugTracePanel trace={msg.metadata.debug_trace} />
                )}
              </React.Fragment>
            ))
          )}
          {simulator.isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Audio preview bar */}
        {recordedAudio && (
          <div className="simulator-preview-bar">
            <audio controls src={recordedAudio} preload="metadata" />
            <div className="simulator-preview-bar__actions">
              <button
                className="simulator-action-btn simulator-action-btn--send"
                onClick={handleSendAudio}
                disabled={simulator.isTyping}
                title="Enviar áudio"
              >
                <IconSend size={16} />
              </button>
              <button
                className="simulator-action-btn simulator-action-btn--cancel"
                onClick={clearRecordedAudio}
                title="Descartar áudio"
              >
                <IconX size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Image preview bar */}
        {imagePreview && (
          <div className="simulator-preview-bar">
            {imageFile?.type === 'application/pdf' ? (
              <div className="simulator-preview-bar__pdf-icon" style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fee2e2', borderRadius: 8, fontSize: 22 }}>PDF</div>
            ) : (
              <img src={imagePreview} alt="Preview" className="simulator-preview-bar__image" />
            )}
            <span className="simulator-preview-bar__name">{imageFile?.name}</span>
            <div className="simulator-preview-bar__actions">
              <button
                className="simulator-action-btn simulator-action-btn--send"
                onClick={handleSendImage}
                disabled={simulator.isTyping}
                title="Enviar imagem"
              >
                <IconSend size={16} />
              </button>
              <button
                className="simulator-action-btn simulator-action-btn--cancel"
                onClick={clearSelectedImage}
                title="Remover imagem"
              >
                <IconX size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="simulator-input">
          <input type="file" ref={fileInputRef} hidden accept="image/*,application/pdf" onChange={handleImageSelect} />

          <button
            className="simulator-action-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={simulator.isTyping || isRecording || !!audioBlob}
            title="Enviar imagem"
          >
            <IconPhoto size={20} />
          </button>

          {isRecording ? (
            <button
              className="simulator-action-btn simulator-action-btn--recording"
              onClick={stopRecording}
              title="Parar gravação"
            >
              <IconPlayerStop size={20} />
            </button>
          ) : (
            <button
              className="simulator-action-btn"
              onClick={startRecording}
              disabled={simulator.isTyping || !!imageFile || !!audioBlob}
              title="Gravar áudio"
            >
              <IconMicrophone size={20} />
            </button>
          )}

          <input
            ref={inputRef}
            type="text"
            placeholder={isRecording ? 'Gravando...' : 'Digite uma mensagem...'}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={simulator.isTyping || isRecording || hasMedia}
          />
          <button
            className="simulator-send"
            onClick={handleSend}
            disabled={!inputValue.trim() || simulator.isTyping || hasMedia}
          >
            <IconSend size={18} />
          </button>
        </div>

        {/* Public Link Modal */}
        <PublicLinkModal
          show={showPublicLinkModal}
          onHide={() => setShowPublicLinkModal(false)}
          agent={{
            id: agent?.id,
            agent_name: agent?.identity_config?.agent_name || agent?.name,
          }}
        />

        {/* Sessions Modal — lista de sessões de teste arquivadas */}
        <PlaygroundSessionsModal
          show={sessionsModalOpen}
          onHide={() => setSessionsModalOpen(false)}
          agentId={agent?.id}
          onReplay={handleReplaySession}
          isReplaying={isReplaying}
        />
      </div>

      {/* Drawer de variáveis — renderizado como IRMÃO do simulator-container,
          dentro do simulator-copilot-wrapper. Assim o .sim-panel expande
          horizontalmente (via :has(.variables-drawer--open)) e o chat
          mantém sua largura original — não fica espremido. Mesmo padrão
          arquitetural do CopilotDrawer. */}
      {variablesPanelOpen && (
        <VariablesPanel
          isOpen={variablesPanelOpen}
          onClose={() => setVariablesPanelOpen(false)}
          variables={collectedVariables}
          collectedData={collectedData}
          apiResponses={apiResponses}
          declaredVariables={declaredVariables}
          recentlyChangedKeys={recentlyChangedKeys}
          onReset={resetCollectedVariables}
        />
      )}

      {copilotOpen && (
        <CopilotDrawer
          agent={agent}
          isOpen={copilotOpen}
          onClose={() => { setCopilotOpen(false); setCopilotFocusedMessage(null); }}
          focusedMessage={copilotFocusedMessage}
          simulatorMessages={simulator.messages}
          clearKey={copilotClearKey}
        />
      )}
    </div>
  );
};

// ===============================================
// SIMULATOR TOGGLE BUTTON (DEPRECATED)
// ===============================================

/** @deprecated Use SimulatorPanel split panel instead */
export const SimulatorToggle = ({ onClick }) => (
  <button className="simulator-toggle" onClick={onClick}>
    <IconRobot size={28} />
  </button>
);

export default AgentSimulator;
